/**
 * AdminAuditDownloads — Registro audit firmato dei download acconsentiti.
 *
 * Mostra:
 * - Filtri per email utente / fornitore / data da-a
 * - Tabella con: data, utente, studio, fornitore, file, IP, signature short
 * - Azioni per riga: "PDF audit" (singola voce firmata) + "Apri scheda asset"
 * - Bottone "Esporta PDF" che genera la lista filtrata come PDF
 *
 * Endpoint:
 * - GET /api/admin/downloads/audit?user_email=..&supplier_id=..&from_date=YYYY-MM-DD&to_date=..&limit=200
 * - GET /api/admin/downloads/audit/{aid}/pdf (single audit PDF)
 * - GET /api/admin/downloads/audit/export.pdf (filtered list as PDF)
 */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  FileText, FileSignature, Download, RefreshCw, Search, Filter, ShieldCheck,
} from "lucide-react";
import { api, API } from "../lib/api";

const PAGE_SIZE = 200;

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtSize(n) {
  const x = Number(n || 0);
  if (!x) return "—";
  if (x < 1024 * 1024) return `${(x / 1024).toFixed(0)} KB`;
  return `${(x / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminAuditDownloads() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  // Filtri
  const [fEmail, setFEmail] = useState("");
  const [fSupplier, setFSupplier] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});
  // Suppliers per dropdown
  const [suppliers, setSuppliers] = useState([]);

  // Build query string da filtri applicati + paginazione
  const buildQuery = useCallback((extraSkip = 0) => {
    const qs = new URLSearchParams();
    qs.set("limit", String(PAGE_SIZE));
    qs.set("skip", String(extraSkip));
    if (appliedFilters.user_email) qs.set("user_email", appliedFilters.user_email);
    if (appliedFilters.supplier_id) qs.set("supplier_id", appliedFilters.supplier_id);
    if (appliedFilters.from_date) qs.set("from_date", appliedFilters.from_date);
    if (appliedFilters.to_date) qs.set("to_date", appliedFilters.to_date);
    return qs.toString();
  }, [appliedFilters]);

  const reload = useCallback(async (newSkip = 0) => {
    setLoading(true);
    try {
      const qs = buildQuery(newSkip);
      const r = await api.get(`/admin/downloads/audit?${qs}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSkip(newSkip);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento registro audit");
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  // Carica fornitori una sola volta per dropdown
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/suppliers");
        setSuppliers((r.data || []).map((s) => ({ id: s.id, name: s.name })).sort((a, b) => a.name.localeCompare(b.name)));
      } catch { /* no-op */ }
    })();
  }, []);

  useEffect(() => {
    reload(0);
  }, [reload]);

  function applyFilters() {
    setAppliedFilters({
      user_email: fEmail.trim().toLowerCase(),
      supplier_id: fSupplier,
      from_date: fFrom,
      to_date: fTo,
    });
  }

  function resetFilters() {
    setFEmail(""); setFSupplier(""); setFFrom(""); setFTo("");
    setAppliedFilters({});
  }

  function openSinglePdf(it) {
    // L'endpoint usa httpOnly cookie auth — apri in nuova tab
    window.open(`${API}/admin/downloads/audit/${it.id}/pdf`, "_blank", "noopener");
  }

  async function exportListPdf() {
    try {
      const qs = buildQuery(0).replace(`skip=${skip}`, "skip=0");
      // Override limit per esportare fino a 5000 voci filtrate
      const qsExport = qs.replace(`limit=${PAGE_SIZE}`, "limit=5000");
      const r = await api.get(`/admin/downloads/audit/export.pdf?${qsExport}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `archi-hub-audit-download-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore esportazione PDF");
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-audit-downloads">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-gold flex items-center gap-2">
            <ShieldCheck size={12} /> Registro Audit · Download Acconsentiti
          </div>
          <h2 className="mt-1 text-2xl font-light text-ink">Registro firmato dei download</h2>
          <p className="text-sm text-slate1 mt-1 max-w-2xl">
            {`Ogni download non-admin di un documento riservato è accompagnato dall'accettazione del disclaimer. La voce è firmata con HMAC-SHA256 e immutabile in DB. Ogni voce è esportabile come PDF formale per uso legale.`}
          </p>
        </div>
        <button
          onClick={exportListPdf}
          disabled={loading || items.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="audit-export-list-btn"
        >
          <Download size={13} /> Esporta lista PDF
        </button>
      </div>

      {/* Filtri */}
      <div className="bg-white border border-ink/10 rounded-sm p-4">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-slate1 mb-3">
          <Filter size={11} /> Filtri
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-[10px] tracking-[0.15em] uppercase text-slate1 mb-1">Email utente</label>
            <input
              type="text" value={fEmail} onChange={(e) => setFEmail(e.target.value)}
              placeholder="trader@studio.it"
              className="w-full border border-ink/15 rounded-sm px-3 py-2 text-sm bg-cream focus:outline-none focus:border-ink"
              data-testid="audit-filter-email"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.15em] uppercase text-slate1 mb-1">Fornitore</label>
            <select
              value={fSupplier} onChange={(e) => setFSupplier(e.target.value)}
              className="w-full border border-ink/15 rounded-sm px-3 py-2 text-sm bg-cream focus:outline-none focus:border-ink"
              data-testid="audit-filter-supplier"
            >
              <option value="">Tutti</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.15em] uppercase text-slate1 mb-1">Dal</label>
            <input
              type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)}
              className="w-full border border-ink/15 rounded-sm px-3 py-2 text-sm bg-cream focus:outline-none focus:border-ink"
              data-testid="audit-filter-from"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.15em] uppercase text-slate1 mb-1">Al</label>
            <input
              type="date" value={fTo} onChange={(e) => setFTo(e.target.value)}
              className="w-full border border-ink/15 rounded-sm px-3 py-2 text-sm bg-cream focus:outline-none focus:border-ink"
              data-testid="audit-filter-to"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={applyFilters}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90"
              data-testid="audit-filter-apply"
            >
              <Search size={12} /> Cerca
            </button>
            <button
              onClick={resetFilters}
              className="inline-flex items-center justify-center px-3 py-2 border border-ink/15 text-[11px] tracking-[0.2em] uppercase text-slate1 hover:bg-ink/5 rounded-sm"
              title="Reset filtri"
              data-testid="audit-filter-reset"
            >
              <RefreshCw size={12} />
            </button>
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white border border-ink/10 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-ink/10 flex items-center justify-between">
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">
            {loading ? "Caricamento…" : `${total} voci totali · ${items.length} visualizzate`}
          </div>
          <div className="text-[10px] tracking-[0.15em] uppercase text-slate1/70">
            Disclaimer v1.0 · HMAC-SHA256
          </div>
        </div>
        {loading ? (
          <div className="px-4 py-12 text-center text-sm text-slate1">Caricamento registro…</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-slate1">
            Nessuna voce nel registro per i filtri selezionati.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream2 text-[10px] tracking-[0.15em] uppercase text-slate1">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Data</th>
                  <th className="text-left px-3 py-2 font-medium">Utente</th>
                  <th className="text-left px-3 py-2 font-medium">Studio</th>
                  <th className="text-left px-3 py-2 font-medium">Fornitore</th>
                  <th className="text-left px-3 py-2 font-medium">File</th>
                  <th className="text-left px-3 py-2 font-medium">Tipo</th>
                  <th className="text-left px-3 py-2 font-medium">Size</th>
                  <th className="text-left px-3 py-2 font-medium">IP</th>
                  <th className="text-left px-3 py-2 font-medium">Signature</th>
                  <th className="text-right px-3 py-2 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-ink/5 hover:bg-cream2/50" data-testid={`audit-row-${it.id}`}>
                    <td className="px-3 py-2 text-ink whitespace-nowrap">{fmtDateTime(it.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="text-ink">{it.user_email}</div>
                      <div className="text-[11px] text-slate1">
                        {it.user_name}
                        {it.account_type === "external" && (
                          <span className="ml-1.5 px-1 py-0.5 bg-gold/15 border border-gold/40 text-gold text-[9px] tracking-[0.1em] uppercase rounded-sm" data-testid={`audit-external-badge-${it.id}`}>
                            External
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-ink">{it.studio || "—"}</td>
                    <td className="px-3 py-2 text-ink">{it.supplier_name || "—"}</td>
                    <td className="px-3 py-2 text-ink max-w-[260px]" title={it.asset_kind === "share_link" ? `${it.asset_title} — ${it.asset_filename}` : it.asset_filename}>
                      {it.asset_kind === "share_link" ? (
                        <>
                          <div className="truncate">{it.asset_title || "Link condiviso"}</div>
                          <div className="text-[11px] text-slate1 truncate">{it.asset_filename}</div>
                        </>
                      ) : (
                        <div className="truncate">{it.asset_filename || it.asset_title || "—"}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate1 uppercase">{it.asset_kind}</td>
                    <td className="px-3 py-2 text-[11px] text-slate1 whitespace-nowrap">{fmtSize(it.asset_size)}</td>
                    <td className="px-3 py-2 text-[11px] text-slate1 font-mono">{it.ip || "—"}</td>
                    <td className="px-3 py-2 text-[10px] text-slate1 font-mono" title={it.signature}>
                      {(it.signature || "").slice(0, 12)}…
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => openSinglePdf(it)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] tracking-[0.15em] uppercase bg-ink text-cream hover:bg-ink/90 rounded-sm"
                        title="Scarica PDF audit firmato per questa voce"
                        data-testid={`audit-pdf-${it.id}`}
                      >
                        <FileSignature size={11} /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginazione */}
        {total > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-ink/10 flex items-center justify-between text-[11px] tracking-[0.15em] uppercase text-slate1">
            <span>{skip + 1}–{Math.min(skip + items.length, total)} di {total}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => reload(Math.max(0, skip - PAGE_SIZE))}
                disabled={skip === 0}
                className="px-3 py-1.5 border border-ink/15 rounded-sm hover:bg-ink/5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ‹ Precedente
              </button>
              <button
                onClick={() => reload(skip + PAGE_SIZE)}
                disabled={skip + PAGE_SIZE >= total}
                className="px-3 py-1.5 border border-ink/15 rounded-sm hover:bg-ink/5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Successiva ›
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="text-[10px] tracking-[0.15em] uppercase text-slate1/70 flex items-start gap-2">
        <FileText size={11} className="mt-0.5 shrink-0" />
        <span>
          Ogni voce è firmata digitalmente con HMAC-SHA256 sui campi
          {" "}id|created_at|user_id|user_email|asset_id|supplier_id|disclaimer_version|ip
          {" "}e archiviata immutabile. La firma è verificabile riapplicando la stessa funzione
          {" "}al payload usando la chiave server-side.
        </span>
      </div>
    </div>
  );
}
